import { createColumnHelper } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { PriceListRow } from "@/shared/api/inventory";

const columnHelper = createColumnHelper<DeskTableFeatures, PriceListRow>();

export type PriceListColumnsOpts = {
  busy: boolean;
  selectedId: string | null;
  selectedItemCount: number;
  onSelect: (id: string) => void;
  onRename: (row: PriceListRow, nextName: string) => void;
  onSetDefaultPos: (row: PriceListRow) => void;
  onDeactivate: (row: PriceListRow) => void;
  onReactivate: (row: PriceListRow) => void;
};

export function priceListColumns(opts: PriceListColumnsOpts) {
  return columnHelper.columns([
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 220,
      cell: ({ row }) => {
        const list = row.original;
        return (
          <div className="min-w-0">
            <Input
              className={cn(
                opts.selectedId === list.id ? "font-semibold" : "font-medium",
              )}
              key={`${list.id}-${list.name}`}
              defaultValue={list.name}
              disabled={opts.busy || list.deactivatedAt != null}
              onFocus={() => opts.onSelect(list.id)}
              onBlur={(e) => opts.onRename(list, e.target.value)}
            />
            <button
              type="button"
              className="mt-0.5 text-left text-xs text-[var(--color-muted)] underline-offset-2 hover:underline"
              onClick={() => opts.onSelect(list.id)}
            >
              {list.code}
              {list.isDefaultPos ? " · POS default" : ""}
              {list.deactivatedAt ? " · deactivated" : ""}
              {opts.selectedItemCount > 0 && opts.selectedId === list.id
                ? ` · ${opts.selectedItemCount} priced`
                : ""}
              {" · edit prices"}
            </button>
          </div>
        );
      },
    }),
    columnHelper.accessor("code", {
      id: "code",
      header: "Code",
      size: 100,
      cell: (info) => (
        <span className="font-mono text-sm">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("isDefaultPos", {
      id: "isDefaultPos",
      header: "POS default",
      size: 110,
      cell: ({ row }) =>
        row.original.isDefaultPos ? (
          <span className="text-xs">Yes</span>
        ) : (
          <span className="text-xs text-[var(--color-muted)]">—</span>
        ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 200,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const list = row.original;
        return (
          <div className="flex flex-wrap justify-end gap-1">
            {!list.isDefaultPos && list.deactivatedAt == null ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy}
                onClick={() => opts.onSetDefaultPos(list)}
              >
                POS default
              </Button>
            ) : null}
            {list.deactivatedAt == null && !list.isDefaultPos ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={opts.busy}
                onClick={() => opts.onDeactivate(list)}
              >
                Deactivate
              </Button>
            ) : null}
            {list.deactivatedAt != null ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy}
                onClick={() => opts.onReactivate(list)}
              >
                Reactivate
              </Button>
            ) : null}
          </div>
        );
      },
    }),
  ]);
}
